import { type ValidationRule, type RoutineView } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

const INVALID_CHILD_KINDS = [
  'AddOffset',
  'MoveArmTo',
  'MoveArmToV2',
  'PressButton',
  'WaitForConfirmation',
] as const;

class RunInBackgroundRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunInBackground', 'RunInBackground');
  }

  protected validate({ stepId, routine }: CanonicalValidateArgs): void {
    const idToNode = this.buildIdMap(routine);
    const hasInvalid = this.hasInvalidChildDeep(stepId, idToNode);

    if (hasInvalid) {
      this.throwInvalidChildren();
    }
  }

  /**
   * Build a map of step ids to nodes.
   * @param routine - The routine.
   * @returns The map of step ids to nodes.
   */
  private buildIdMap(
    routine: RoutineView & {
      derived?: {
        stepById?: Record<string, { stepKind?: string; children?: string[] }>;
      };
    },
  ): Record<string, { stepKind?: string; children?: string[] }> {
    return routine.derived?.stepById ?? {};
  }

  /**
   * Check if the step has invalid children.
   * @param id - The id of the step.
   * @param idToNode - The map of step ids to nodes.
   * @returns True if the step has invalid children, false otherwise.
   */
  private hasInvalidChildDeep(
    id: string,
    idToNode: Record<string, { stepKind?: string; children?: string[] }>,
  ): boolean {
    const node = idToNode[id];
    if (!node) return false;

    if (
      (node.children ?? []).some((cid: string | number) =>
        (INVALID_CHILD_KINDS as readonly string[]).includes(
          idToNode[cid]?.stepKind ?? '',
        ),
      )
    ) {
      return true;
    }

    return (node.children ?? []).some((cid: string) =>
      this.hasInvalidChildDeep(cid, idToNode),
    );
  }

  /**
   * Throw an error if the step has invalid children.
   */
  private throwInvalidChildren(): never {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message:
        'Run in Background steps may not contain Add Offset, Move Arm, Press Button or Prompt User steps.',
    });
  }
}

export const RunInBackgroundValidation: ValidationRule =
  new RunInBackgroundRule().toValidationRule();
